import React, { useState, useEffect, useRef } from "react";
import { Search, Menu, X, UserCircle, LogOut, LogIn, MessageCircle, ChevronDown, ShoppingCart } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../../1122.png";

const apiUrl = process.env.REACT_APP_API_URL;

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const userDropdownRef = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token || token === "null") return;
      try {
        const res = await fetch(`${apiUrl}/api/messages/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setUnreadCount(data.count || 0);
      } catch (e) { }
    };

    fetchUnread();
    const interval = setInterval(fetchUnread, 10000); // Poll every 10s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const uStr = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (uStr) {
      try {
        const u = JSON.parse(uStr);
        if (u) setUser(u);
      } catch (e) { }
    }
  }, []);

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) {
        setUserOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!search || search.length < 2) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      const res = await fetch(
        `${apiUrl}/api/search/suggestions?q=${search}`
      );
      const data = await res.json();
      setSuggestions(data);
      setLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login");
  };
  const [query, setQuery] = useState("");

  const handleSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    navigate(`/search?q=${encodeURIComponent(search)}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
      {/* TOP BAR */}
      <div className="px-4 py-3 flex items-center justify-between">
        {/* LEFT */}
        <div className="flex items-center gap-3">
          <button className="md:hidden" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X /> : <Menu />}
          </button>

          <Link to="/" className="text-2xl font-bold text-green-600 flex items-center gap-2">
            <img src={logo} alt="logo" className="w-10 h-10" />
            HuluOrder
          </Link>
        </div>

        {/* DESKTOP SEARCH */}
        <div className="hidden md:flex relative w-1/3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search electronics"
            className="w-full rounded-xl border px-4 py-2 pl-10"
          />
          <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />

          {suggestions.length > 0 && (
            <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow z-50">
              {suggestions.map((s) => (
                <Link
                  key={s.id}
                  to={`/detail/${s.id}`}
                  className="block px-4 py-3 hover:bg-gray-100"
                  onClick={() => setSuggestions([])}
                >
                  <p className="font-medium">{s.title}</p>
                  {s.brand && (
                    <p className="text-sm text-gray-500">{s.brand}</p>
                  )}
                </Link>
              ))}

              <Link
                to={`/search?q=${search}`}
                className="block px-4 py-3 text-center text-green-600 font-medium border-t"
              >
                View all results →
              </Link>
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div ref={userDropdownRef} className="relative flex items-center gap-3">
          {/* Place Order — visible to everyone */}
          <Link
            to="/place-order"
            className="hidden lg:flex items-center gap-1.5 border border-green-600 text-green-600 hover:bg-green-50 px-4 py-1.5 rounded-xl font-bold text-sm transition"
          >
            <ShoppingCart className="w-4 h-4" />
            Place Order
          </Link>

          {user ? (
            <div className="flex flex-row gap-3 items-center">
              <Link
                to="/messages"
                className="relative p-2 text-gray-500 hover:text-green-600 transition"
              >
                <MessageCircle className="w-6 h-6" />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-white">
                    {unreadCount}
                  </span>
                )}
              </Link>
              <Link
                to="/PostItemPage"
                className="hidden lg:block bg-green-600 hover:bg-green-700 text-white text-center px-5 py-1.5 rounded-xl font-bold text-sm transition shadow-sm"
                onClick={() => setMenuOpen(false)}
              >
                Post Item
              </Link>
              <div className="flex items-center gap-2 bg-gray-50 rounded-full pl-1 pr-2 py-1 border border-gray-100 hover:border-gray-200 transition-colors">
                <Link
                  to="/profile"
                  className="flex items-center gap-2"
                >
                  <img
                    src={user.picture || `https://ui-avatars.com/api/?name=${user.name}`}
                    alt="profile"
                    className="w-8 h-8 rounded-full border border-white shadow-sm"
                  />
                  <span className="hidden md:block font-semibold text-sm text-gray-700 truncate max-w-[100px]">{user.name}</span>
                </Link>
                <button
                  onClick={() => setUserOpen(!userOpen)}
                  className={`p-1 rounded-full transition-all duration-200 ${userOpen ? "bg-green-100 text-green-600 rotate-180" : "text-gray-400 hover:bg-gray-100"}`}
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>
          ) : (
            <Link to="/login">
              <button className="bg-green-600 text-white px-4 py-2 rounded-xl font-semibold text-sm">
                Login
              </button>
            </Link>
          )}

          {/* DESKTOP USER DROPDOWN */}
          {userOpen && user && (
            <div className="hidden md:block absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-100 z-50">
              <Link
                to="/profile"
                className="block px-4 py-2 hover:bg-gray-100"
                onClick={() => setUserOpen(false)}
              >
                Profile
              </Link>
              <Link
                to="/my-posts"
                className="block px-4 py-2 hover:bg-gray-100"
                onClick={() => setUserOpen(false)}
              >
                My Posts
              </Link>
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  className="block px-4 py-2 hover:bg-green-50 text-green-600 font-bold"
                  onClick={() => setUserOpen(false)}
                >
                  Admin Panel
                </Link>
              )}
              <Link
                to="/messages"
                className="block px-4 py-2 hover:bg-gray-100"
                onClick={() => setUserOpen(false)}
              >
                Messages
              </Link>
              <button
                onClick={logout}
                className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* MOBILE SEARCH */}
      <div className="px-4 pb-3 md:hidden">
        <form onSubmit={submitSearch} className="relative w-full">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search electronics"
            className="w-full rounded-xl border px-4 py-2 pl-10"
          />
          <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
        </form>
      </div>

      {/* MOBILE MENU */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t px-4 py-4 space-y-3">
          <Link
            to="/place-order"
            className="block border border-green-600 text-green-600 text-center py-3 rounded-xl font-bold transition"
            onClick={() => setMenuOpen(false)}
          >
            Place Order
          </Link>
          <Link
            to="/PostItemPage"
            className="block bg-green-600 hover:bg-green-700 text-white text-center py-3 rounded-xl font-bold transition shadow-sm"
            onClick={() => setMenuOpen(false)}
          >
            Post Item
          </Link>

          {user ? (
            <>
              <Link
                to="/profile"
                className="block py-2"
                onClick={() => setMenuOpen(false)}
              >
                Profile
              </Link>
              <Link
                to="/my-posts"
                className="block py-2"
                onClick={() => setMenuOpen(false)}
              >
                My Posts
              </Link>
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  className="block py-2 text-green-600 font-bold"
                  onClick={() => setMenuOpen(false)}
                >
                  Admin Panel
                </Link>
              )}
              <Link
                to="/messages"
                className="flex items-center justify-between py-2"
                onClick={() => setMenuOpen(false)}
              >
                <span>Messages</span>
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    {unreadCount} New
                  </span>
                )}
              </Link>
              <button
                onClick={logout}
                className="w-full text-left py-2 text-red-600"
              >
                Logout
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="block py-2 text-green-600"
              onClick={() => setMenuOpen(false)}
            >
              Login
            </Link>
          )}
        </div>
      )}
    </header>
  );
}

export default Header;
